<template>
    <el-container direction="vertical" style="margin: 5vh 5vw">
        <user-new :isVisible.sync="showNewModal" />
        <user-edit :isVisible.sync="showEditModal" :userSelected="userSelected" />
        <div class="header" style="margin-bottom: 25px">
            <h3>
                Manage Users
                <el-tooltip placement="right" effect="light">
                    <span slot="content"
                        >DataMaestro users are added and managed by the DataMaestro<br />administrators. These accounts
                        are not linked to Planning Analytics<br />users.</span
                    >
                    <i
                        class="el-icon-info"
                        style="
                            position: relative;
                            left: 2px;
                            bottom: 12px;
                            font-size: 1rem;
                        "
                    />
                </el-tooltip>
            </h3>
        </div>
        <el-table
            :data="itemsToShow"
            :default-sort="{ prop: 'displayName', order: 'ascending' }"
            style="border: 2px solid #ebeef5; width: 100%"
        >
            <el-table-column width="200">
                <template slot="header">
                    <el-button @click="showNewModal = true" class="button" icon="el-icon-plus">Add User</el-button>
                </template>
                <template slot-scope="scope">
                    <el-button-group>
                        <el-tooltip placement="bottom" content="Edit User Profile">
                            <el-button
                                size="medium"
                                class="table-button"
                                icon="el-icon-edit"
                                @click="
                                    showEditModal = true
                                    userSelected = scope.row
                                "
                            />
                        </el-tooltip>
                        <el-tooltip placement="bottom" content="Force Password Reset" v-if="scope.row.id !== userId">
                            <el-button
                                size="medium"
                                class="table-button"
                                icon="el-icon-lock"
                                @click="clearPassword(scope.row)"
                            />
                        </el-tooltip>
                        <el-button size="medium" icon="el-icon-lock" class="hidden" v-else />
                        <el-tooltip placement="bottom" v-if="scope.row.id !== userId">
                            <span slot="content">{{ scope.row.active ? 'Deactivate' : 'Activate' }} User</span>
                            <el-button
                                size="medium"
                                class="table-button"
                                :icon="`el-icon-${scope.row.active ? 'open' : 'turn-off'}`"
                                @click="updateStatus(scope.row)"
                        /></el-tooltip>
                        <el-button
                            size="medium"
                            :icon="`el-icon-${scope.row.active ? 'open' : 'turn-off'}`"
                            class="hidden"
                            v-else
                        />
                        <el-tooltip placement="bottom" content="Delete User" v-if="scope.row.id !== userId">
                            <el-button
                                size="medium"
                                class="table-button"
                                icon="el-icon-delete"
                                @click="deleteUser(scope.row)"
                            />
                        </el-tooltip>
                        <el-button size="medium" icon="el-icon-delete" class="hidden" v-else />
                    </el-button-group>
                </template>
            </el-table-column>
            <el-table-column prop="firstName" label="First Name" sortable :min-width="15" />
            <el-table-column prop="lastName" label="Last Name" sortable :min-width="15" />
            <el-table-column prop="displayName" label="Display Name" sortable :min-width="25" />
            <el-table-column prop="email" label="Email" sortable :min-width="30" />
            <el-table-column label="Role" sortable :min-width="15">
                <template slot-scope="scope">
                    <span>{{ scope.row.role === 'Standard' ? 'Standard User' : scope.row.role }}</span>
                </template></el-table-column
            >
        </el-table>
    </el-container>
</template>

<script>
import AdministrationService from '../services/AdministrationService'
import { mapState } from 'vuex'
import UserNew from '../components/modals/UserNew'
import UserEdit from '../components/modals/UserEdit'
import Helper from '..//utilities/Helper'

/**
 * @vue-computed {Helper} mapState - Maps Vuex state to computed properties
 * @vue-data {Object} [userSelected=null] - User object to be updated
 * @vue-data {Array} [items=[]] - Current list of users
 * @vue-data {String} [sortBy="name"] - Current field to sort by
 * @vue-data {String} [error=null] - Error message to display
 * @vue-data {Number} [dismiss=0] - Countdown to dismiss the alert message
 */
export default {
    computed: {
        ...mapState(['organizationId', 'userId', 'isUserLoggedIn', 'userRole', 'instanceId']),
    },
    components: { UserNew, UserEdit },
    watch: {
        showEditModal(value) {
            if (!value) this.getUsers()
        },
        showNewModal(value) {
            if (!value) this.getUsers()
        },
    },
    data() {
        return {
            userSelected: null,
            showEditModal: false,
            showNewModal: false,
            items: [],
            itemsToShow: [],
            sortBy: 'name',
        }
    },
    methods: {
        handleError: Helper.handleError,
        /**
         * Activates or deactivates the user
         * @async
         * @function
         * @param {Object} row - Table row of the selected user
         */
        async updateStatus(row) {
            if (confirm(`Are you sure you want to ${row.active ? 'deactivate' : 'activate'} ${row.displayName}?`)) {
                const loading = this.$loading({
                    lock: true,
                    //text: "Working",
                    background: 'rgba(0, 0, 0, 0.3)',
                })
                try {
                    await AdministrationService.updateStatus({
                        userId: row.id,
                    })
                    if (row.active) {
                        this.$message({
                            type: 'success',
                            message: `${row.displayName}'s DataMaestro account has been deactivated. They will not be able to log in to DataMaestro until it is reactivated.`,
                            duration: 5000,
                            showClose: true,
                        })
                    } else {
                        this.$message({
                            type: 'success',
                            message: `${row.displayName}'s DataMaestro account activated successfully`,
                            duration: 5000,
                            showClose: true,
                        })
                    }
                    this.getUsers()
                } catch (err) {
                    this.handleError(err, this, this.instanceId)
                } finally {
                    loading.close()
                }
            }
        },
        /**
         * Clears the user's password
         * @function
         * @param {Number} dismiss - Time remaining
         */
        async clearPassword(row) {
            if (confirm(`Are you sure you want to force ${row.displayName} to reset their password?`)) {
                const loading = this.$loading({
                    lock: true,
                    //text: "Working",
                    background: 'rgba(0, 0, 0, 0.3)',
                })
                try {
                    await AdministrationService.clearPassword({
                        userId: row.id,
                    })
                    this.$message({
                        type: 'success',
                        message: `${row.displayName}'s password has been cleared out. They will need to reset it in order to log back in to DataMaestro.`,
                        duration: 5000,
                        showClose: true,
                    })
                } catch (err) {
                    this.handleError(err, this, this.instanceId)
                } finally {
                    loading.close()
                }
            }
        },
        /**
         * Updates the alert countdown
         * @function
         * @param {Number} dismiss - Time remaining
         */
        dismissChanged(dismiss) {
            this.dismiss = dismiss
        },
        /**
         * Deletes the user
         * @async
         * @function
         * @param {Object} row - Table row of the selected user
         */
        async deleteUser(row) {
            if (confirm(`Are you sure you want to delete ${row.displayName}?`)) {
                const loading = this.$loading({
                    lock: true,
                    //text: "Working",
                    background: 'rgba(0, 0, 0, 0.3)',
                })
                try {
                    await AdministrationService.delete({
                        userId: row.id,
                    })
                    this.getUsers()
                    this.$message({
                        type: 'success',
                        message: `${row.displayName} deleted successfully`,
                        duration: 5000,
                        showClose: true,
                    })
                } catch (err) {
                    this.handleError(err, this, this.instanceId)
                } finally {
                    loading.close()
                }
            }
        },
        /**
         * @async
         */
        async getUsers() {
            const loading = this.$loading({
                lock: true,
                //text: "Working",
                background: 'rgba(0, 0, 0, 0.3)',
            })
            try {
                const response = await AdministrationService.show({
                    organizationId: this.organizationId,
                })
                this.items = response.data.users
                this.itemsToShow = this.items
            } catch (err) {
                this.handleError(err, this, this.instanceId)
            } finally {
                loading.close()
            }
        },
    },
    mounted() {
        if (!this.isUserLoggedIn) {
            this.$router.push({ name: 'login' })
        } else if (this.userRole !== 'Administrator') {
            this.$router.push({
                name: 'tasks',
                params: { instanceId: this.instanceId },
            })
        } else {
            this.getUsers()
        }
    },
}
</script>

<style scoped>
.header {
    display: flex;
    justify-content: space-between;
}
.button {
    background-color: #b3282d;
    color: white;
    border-color: white;
    padding: 10px 15px;
}
.button:focus {
    background-color: #9a0f14;
    color: white;
    border-color: white;
    outline: none;
}
.button:hover {
    background-color: #9a0f14;
    color: white;
    border-color: white;
    outline: none;
}
.table-button {
    background-color: inherit;
    color: #606266;
    border: none;
    outline: none;
    font-size: 1.15rem;
}
.table-button:focus {
    background-color: inherit;
    color: #606266;
    border: none;
    outline: none;
}
.table-button:hover {
    background-color: #ecf5fe;
    color: #606266;
    border: none;
    outline: none;
}
.tooltip .tooltip-inner {
    max-width: 300px;
    width: 300px;
}
.hidden:focus {
    background-color: inherit;
    color: #f0f0f0;
    border: none;
    outline: none;
}
.hidden:hover {
    background-color: inherit;
    color: #f0f0f0;
    border: none;
    outline: none;
}
.hidden {
    background-color: inherit;
    color: #f0f0f0;
    border: none;
    outline: none;
    font-size: 1.15rem;
}
</style>
